import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownButton, DropdownMenu, DropdownMenuItem } from "@unified-checkout/design-system";
import type { IChannel } from "@app/dashboard/types/innerLayout/home/overview/channels";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { useFiltersStore } from "@app/dashboard/store/home/filters";
import { useIntegrationsStore } from "@app/dashboard/store/integrations";
import { getChannels } from "@app/dashboard/services/inner_layout/home/overview/channels";
import { getLang } from "@app/dashboard/utils/localStorageUtils";

const Channels = () => {
  const { t } = useTranslation();

  const { filters, setFilters } = useFiltersStore();

  const lang: 'en' | 'ar' = getLang();

  const [selectedChannel, setSelectedChannel] = useState<IChannel>();

  const {
    isLoading,
    isError,
    data: channelsData,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_CHANNELS],
    queryFn: () => getChannels(),
  });

  const { isLoading: isIntegrationsLoading } = useIntegrationsStore();

  const channels: IChannel[] = channelsData?.data.channel;

  useEffect(() => {
    const defaultChannel = channels?.find(channel => channel.default);
    setSelectedChannel(defaultChannel);

    // Set global state with the default channel
    setFilters({
      channel: defaultChannel?.parameter
    });

  }, [channels]);

  const handleClick = (channel: IChannel) => {
    setSelectedChannel(channel);

    // Set global state with the selected channel
    setFilters({
      channel: channel.parameter
    });
  };

  return (
    <Dropdown width="11rem">
      <DropdownButton
        placeholder={t('innerLayout.homePage.overview.channels.selectChannel')}
        text={selectedChannel?.[`name_${lang}`] || ""}
        disabled={isError || isLoading || isIntegrationsLoading}
      />
      <DropdownMenu width="full">
        {
          channels?.map((channel, i: number) =>
            <DropdownMenuItem
              key={`channel-${selectedChannel?.parameter}-${i}`}
              text={channel[`name_${lang}`]}
              selected={selectedChannel?.parameter === channel.parameter}
              onClickHandler={() => handleClick(channel)}
            />
          )
        }
      </DropdownMenu>
    </Dropdown>
  )
};

export default Channels;